import { useRef, useMemo, useEffect, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { vertexShader, fragmentShader, stateColors, progressColors, SphereState } from './shaders/sphereShader';

// Progress data interface
interface ProgressData {
  stage: string;
  percent: number;  // 0-100
}

interface SphereProps {
  audioLevel: number;
  state: SphereState;
  progress?: ProgressData | null;
}

function AnimatedSphere({ audioLevel, state, progress }: SphereProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);

  // Track target colors for smooth transitions
  const [targetColors, setTargetColors] = useState({
    color: new THREE.Vector3(...stateColors.idle.color),
    emissive: new THREE.Vector3(...stateColors.idle.emissive),
  });

  // Uniforms for shader
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uAudioLevel: { value: 0 },
    uNoiseScale: { value: 1.5 },
    uDisplacementScale: { value: 0.5 },
    uColor: { value: new THREE.Vector3(...stateColors.idle.color) },
    uEmissiveColor: { value: new THREE.Vector3(...stateColors.idle.emissive) },
    uProgress: { value: 0 },  // Progress ring 0-1
  }), []);

  // Update target colors when state or progress changes
  useEffect(() => {
    // If progress is active, use progress colors
    if (progress && progress.percent > 0 && progressColors[progress.stage]) {
      const colors = progressColors[progress.stage];
      setTargetColors({
        color: new THREE.Vector3(...colors.color),
        emissive: new THREE.Vector3(...colors.emissive),
      });
    } else {
      // Use state-based colors
      const colors = stateColors[state] || stateColors.idle;
      setTargetColors({
        color: new THREE.Vector3(...colors.color),
        emissive: new THREE.Vector3(...colors.emissive),
      });
    }
  }, [state, progress?.stage, progress?.percent]);

  // Animation loop
  useFrame((_, delta) => {
    if (!materialRef.current) return;

    // Update time
    uniforms.uTime.value += delta;

    // Smooth audio level transition
    const targetAudio = audioLevel / 100; // Normalize to 0-1
    uniforms.uAudioLevel.value = THREE.MathUtils.lerp(
      uniforms.uAudioLevel.value,
      targetAudio,
      0.1
    );

    // Smooth color transitions
    uniforms.uColor.value.lerp(targetColors.color, 0.05);
    uniforms.uEmissiveColor.value.lerp(targetColors.emissive, 0.05);

    // Smooth progress transition
    const targetProgress = progress ? progress.percent / 100 : 0;
    uniforms.uProgress.value = THREE.MathUtils.lerp(
      uniforms.uProgress.value,
      targetProgress,
      0.08  // Smooth but responsive
    );

    // Subtle rotation for visual interest
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.1;
      meshRef.current.rotation.x = Math.sin(uniforms.uTime.value * 0.2) * 0.1;
    }
  });

  return (
    <mesh ref={meshRef}>
      <icosahedronGeometry args={[1, 128]} />
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        side={THREE.FrontSide}
      />
    </mesh>
  );
}

interface AudioSphereProps {
  audioLevel: number;
  state: SphereState;
  className?: string;
  progress?: ProgressData | null;
}

// CSS glow colors for progress stages
const progressGlowColors: Record<string, string> = {
  securing: 'rgba(99, 102, 241, 0.4)',     // Indigo
  decrypting: 'rgba(139, 92, 246, 0.4)',   // Violet
  converting: 'rgba(168, 85, 247, 0.4)',   // Purple
  transcribing: 'rgba(6, 182, 212, 0.4)',  // Cyan
  enhancing: 'rgba(20, 184, 166, 0.4)',    // Teal
  finalizing: 'rgba(34, 197, 94, 0.4)',    // Green
  complete: 'rgba(34, 197, 94, 0.4)',      // Green
};

export default function AudioSphere({ audioLevel, state, className = '', progress }: AudioSphereProps) {
  // Get glow color based on progress stage or state
  const getGlowGradient = () => {
    // If progress is active, use progress stage colors
    if (progress?.stage && progressGlowColors[progress.stage]) {
      return `radial-gradient(circle, ${progressGlowColors[progress.stage]} 0%, transparent 70%)`;
    }
    // Fall back to state-based colors
    if (state === 'recording') {
      return 'radial-gradient(circle, rgba(34, 197, 94, 0.4) 0%, transparent 70%)';
    }
    if (state === 'paused') {
      return 'radial-gradient(circle, rgba(245, 158, 11, 0.4) 0%, transparent 70%)';
    }
    if (state === 'encrypting' || state === 'uploading') {
      return 'radial-gradient(circle, rgba(99, 102, 241, 0.4) 0%, transparent 70%)';
    }
    return 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%)';
  };

  // Get reflection color based on progress stage or state
  const getReflectionGradient = () => {
    // If progress is active, use progress stage colors
    if (progress?.stage && progressGlowColors[progress.stage]) {
      const color = progressGlowColors[progress.stage].replace('0.4)', '0.5)');
      return `linear-gradient(to top, ${color}, transparent)`;
    }
    // Fall back to state-based colors
    if (state === 'recording') {
      return 'linear-gradient(to top, rgba(34, 197, 94, 0.5), transparent)';
    }
    if (state === 'paused') {
      return 'linear-gradient(to top, rgba(245, 158, 11, 0.5), transparent)';
    }
    if (state === 'encrypting' || state === 'uploading') {
      return 'linear-gradient(to top, rgba(99, 102, 241, 0.5), transparent)';
    }
    return 'linear-gradient(to top, rgba(59, 130, 246, 0.5), transparent)';
  };

  return (
    <div className={`relative ${className}`}>
      {/* Glow effect behind sphere */}
      <div
        className="absolute inset-0 rounded-full blur-3xl opacity-40 transition-colors duration-500"
        style={{ background: getGlowGradient() }}
      />

      {/* 3D Canvas */}
      <Canvas
        camera={{ position: [0, 0, 3], fov: 50 }}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: 'high-performance'
        }}
        style={{ background: 'transparent' }}
      >
        {/* Ambient light for overall illumination */}
        <ambientLight intensity={0.4} />

        {/* Point lights for depth and highlights */}
        <pointLight position={[10, 10, 10]} intensity={0.8} color="#ffffff" />
        <pointLight position={[-10, -10, -10]} intensity={0.3} color="#3b82f6" />
        <pointLight position={[0, 10, 0]} intensity={0.4} color="#ffffff" />

        {/* The animated sphere */}
        <AnimatedSphere audioLevel={audioLevel} state={state} progress={progress} />

        {/* Subtle camera controls (limited for UX) */}
        <OrbitControls
          enableZoom={false}
          enablePan={false}
          autoRotate
          autoRotateSpeed={0.3}
          maxPolarAngle={Math.PI / 1.5}
          minPolarAngle={Math.PI / 3}
        />
      </Canvas>

      {/* Reflection effect */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-8 rounded-full blur-xl opacity-20"
        style={{ background: getReflectionGradient() }}
      />
    </div>
  );
}

// Export types for external use
export type { SphereState, ProgressData };
